import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const TIERS = [
  {
    name: "Seeker",
    price: 0,
    tag: "Free forever",
    blurb: "Begin your walk with the community.",
    perks: ["Join prayer groups", "Read testimonies", "RSVP to gatherings", "Comment & react on the feed"],
    cta: "Current",
    paid: false,
  },
  {
    name: "Believer",
    price: 9,
    tag: "Most loved",
    blurb: "Go deeper. Support the community. Share without limits.",
    perks: ["Everything in Seeker", "Create prayer groups", "Share videos in the feed", "Post testimonies with imagery", "Believer badge on profile"],
    cta: "Become a Believer",
    paid: true,
  },
  {
    name: "Disciple",
    price: 19,
    tag: "For shepherds",
    blurb: "For leaders building circles of prayer and community.",
    perks: ["Everything in Believer", "Host gatherings & livestreams", "Pinned testimonies", "Early access to new features", "Disciple badge", "Jesus Just Us chain — earned after 6 months of faithful discipleship (or at year-end, whichever comes first)"],
    cta: "Become a Disciple",
    paid: true,
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const start = async (tier) => {
    if (!user) { navigate("/register"); return; }
    setBusy(tier);
    try {
      const { data } = await api.post("/billing/checkout", { tier, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBusy(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-14">
      <Seo
        title="Membership pricing · Jesus Just Us"
        description="Belong freely as a Seeker. Go deeper as a Believer ($9/mo). Lead as a Disciple ($19/mo). Choose your walk — change anytime."
        path="/pricing"
      />
      <div className="text-center max-w-2xl mx-auto">
        <div className="overline text-burgundy">Membership</div>
        <h1 className="font-serif text-5xl text-jjblue mt-3">Belong freely. Bless to go further.</h1>
        <p className="mt-4 text-[var(--jj-text-secondary)]">Choose where you are in your walk. Upgrade or change anytime.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {TIERS.map((t) => {
          const isCurrent = user?.tier === t.name;
          return (
            <div key={t.name}
              className={`jj-card p-8 flex flex-col ${t.name === "Believer" ? "ring-1 ring-[var(--jj-gold)]" : ""}`}
              data-testid={`tier-${t.name}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-3xl text-jjblue">{t.name}</h3>
                {t.name === "Believer" && <span className="overline text-burgundy flex items-center gap-1"><Sparkles className="h-3 w-3" /> {t.tag}</span>}
              </div>
              <div className="mt-3 text-jjblue">
                <span className="font-serif text-5xl">${t.price}</span>
                <span className="text-sm text-[var(--jj-muted)]"> / month</span>
              </div>
              <p className="mt-3 text-sm text-[var(--jj-text-secondary)]">{t.blurb}</p>
              <ul className="mt-6 space-y-3">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-jjblue/85">
                    <Check className="h-4 w-4 text-gold mt-0.5 shrink-0" strokeWidth={2} /> {p}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                {t.paid ? (
                  <button onClick={() => start(t.name)} disabled={busy === t.name || isCurrent}
                    className={isCurrent ? "btn-secondary w-full" : "btn-primary w-full"} data-testid={`tier-cta-${t.name}`}>
                    {isCurrent ? "Your current tier" : busy === t.name ? "Redirecting…" : t.cta}
                  </button>
                ) : (
                  user ? (
                    <div className="btn-secondary w-full text-center" data-testid={`tier-cta-${t.name}`}>
                      {isCurrent ? "Your current tier" : "Free to all members"}
                    </div>
                  ) : (
                    <Link to="/register" className="btn-secondary w-full inline-block text-center" data-testid={`tier-cta-${t.name}`}>Start free</Link>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-10 text-center text-xs text-[var(--jj-muted)]">Test mode — cards: 4242 4242 4242 4242, any future date, any CVC.</p>
    </div>
  );
}
